import { adminApi, adminSilentApi, userSilentApi } from '../../utils/apiInstances';
import { AuthContext, ApiResponse } from '../types';

export type { AuthContext };

const getSilentApi = (context: AuthContext) => {
    switch (context) {
        case 'admin': return adminSilentApi;
        case 'user': return userSilentApi;
        case 'organizer': return userSilentApi;
        default: return userSilentApi;
    }
};

/**
 * Unified function to get Leagues
 */
export const handleGetLeagues = async (
    context: AuthContext,
    params: Record<string, unknown> = {}
): Promise<ApiResponse> => {
    try {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join("&");

        const endpoint = context === "admin" ? "/admin/admin-leagues" : "/user/leagues";
        const api = getSilentApi(context);
        const uri = `${endpoint}${query ? `?${query}` : ""}`;

        const response = await api("GET", uri);
        return response;
    } catch (error) {
        console.error(`Error fetching leagues (${context}):`, error);
        throw error;
    }
};

/**
 * Unified function to get League by ID (Standings/Details)
 */
export const handleGetLeagueById = async (context: AuthContext, leagueID: string | number): Promise<ApiResponse> => {
    try {
        // Admin: standard CRUD get? No, logic in existing controller wasn't explicit for "Get by ID" except for updates.
        // But User uses `/user/league-standings/${leagueID}`
        // Admin uses `/admin/admin-leagues` list. 
        // Let's assume standard behavior or map specifics.

        const api = getSilentApi(context);
        let endpoint = "";

        if (context === "admin") {
            // If admin needs single league, standard REST
            endpoint = `/admin/admin-leagues/${leagueID}`;
        } else {
            endpoint = `/user/league-standings/${leagueID}`;
        }

        const response = await api("GET", endpoint);
        return response;
    } catch (error) {
        console.error(`Error fetching league by ID (${context}):`, error);
        throw error;
    }
};

/**
 * Admin: League CRUD
 */
export const handleAdminLeagueCRUD = {
    create: async (
        payload: Record<string, unknown>,
        onSuccess: (res: ApiResponse) => void,
        onError?: (err: unknown) => void
    ) => {
        try {
            const response = await adminApi("POST", "/admin/admin-leagues", payload);
            onSuccess(response as ApiResponse);
        } catch (error) {
            if (onError) onError(error);
        }
    },
    delete: async (id: string | number, onSuccess: (res: ApiResponse) => void, onError?: (err: unknown) => void) => {
        try {
            const response = await adminApi("DELETE", `/admin/admin-leagues/${id}`);
            onSuccess(response as ApiResponse);
        } catch (error) {
            if (onError) onError(error);
        }
    }
};

/**
 * Admin: Teams CRUD and Management
 */
export const handleAdminTeamCRUD = {
    getByLeagueId: async (leagueID: string | number): Promise<ApiResponse | undefined> => {
        try {
            return await adminSilentApi("GET", `/admin/admin-league-teams/${leagueID}`) as ApiResponse;
        } catch (error) {
            console.error(error);
            throw error;
        }
    },
    getById: async (teamID: string | number): Promise<ApiResponse | undefined> => {
        try {
            return await adminSilentApi("GET", `/admin/admin-team/${teamID}`) as ApiResponse;
        } catch (error) {
            console.error(error);
            throw error;
        }
    },
    create: async (
        payload: Record<string, unknown>,
        onSuccess: (res: ApiResponse) => void,
        onError?: (err: unknown) => void
    ) => {
        try {
            const response = await adminApi("POST", "/admin/admin-team", payload);
            onSuccess(response as ApiResponse);
        } catch (error) { if (onError) onError(error); }
    },
    update: async (
        id: string | number,
        payload: Record<string, unknown>,
        onSuccess: (res: ApiResponse) => void,
        onError?: (err: unknown) => void
    ) => {
        try {
            const response = await adminApi("PATCH", `/admin/admin-team/${id}`, payload);
            onSuccess(response as ApiResponse);
        } catch (error) { if (onError) onError(error); }
    },
    delete: async (id: string | number, onSuccess: (res: ApiResponse) => void, onError?: (err: unknown) => void) => {
        try {
            const response = await adminApi("DELETE", `/admin/admin-team/${id}`);
            onSuccess(response as ApiResponse);
        } catch (error) { if (onError) onError(error); }
    }
};

/**
 * Admin: League Games (Matches)
 */
export const handleAdminLeagueGameCRUD = {
    getAll: async (params: Record<string, unknown> = {}): Promise<ApiResponse | undefined> => {
        try {
            const query = Object.entries(params)
                .filter(([, value]) => value !== undefined && value !== null && value !== "")
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
                .join("&");
            return await adminSilentApi("GET", `/admin/admin-league-games${query ? `?${query}` : ""}`) as ApiResponse;
        } catch (error) {
            console.error(error);
            throw error;
        }
    },
    getById: async (id: string | number) => {
        try {
            return await adminSilentApi("GET", `/admin/admin-league-games/${id}`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    },
    create: async (
        payload: Record<string, unknown>,
        onSuccess: (res: ApiResponse) => void,
        onError?: (err: unknown) => void
    ) => {
        try {
            const response = await adminApi("POST", "/admin/admin-league-games", payload);
            onSuccess(response as ApiResponse);
        } catch (error) { if (onError) onError(error); }
    },
    update: async (
        id: string | number,
        payload: Record<string, unknown>,
        onSuccess: (res: ApiResponse) => void,
        onError?: (err: unknown) => void
    ) => {
        try {
            const response = await adminApi("PATCH", `/admin/admin-league-games/${id}`, payload);
            onSuccess(response as ApiResponse);
        } catch (error) { if (onError) onError(error); }
    },
    delete: async (id: string | number, onSuccess?: (res: ApiResponse) => void, onError?: (err: unknown) => void) => {
        try {
            const response = await adminApi("DELETE", `/admin/admin-league-games/${id}`);
            if (onSuccess) onSuccess(response as ApiResponse);
        } catch (error) { if (onError) onError(error); }
    }
};
